import { useMutation, useQuery } from "src/Hooks/useReactQuery";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import routeGuard from "src/HOC/routeGuard";
import FormGNCSimulator6DOF from "src/components/GNCSimulator6DOF/form";
import APP_ROUTES from "src/routes/routes";
import {
  createSimulator6DOF,
  editSimulator6DOF,
  getPageIndexSimulator6DOF,
  getSimulatorOptions,
  getSimulatorsDetail6DOF,
} from "src/services/simulator-gnc";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { alertState, dataJson, uploadingJsonFile } from "src/recoil/atoms/global";
import { useTranslation } from "react-i18next";
import { FORM_TYPE, PROCESS_STATUS, RESOURCE_TYPE, ROLES, SIMULATION_TYPE } from "src/constant/common";
import {
  extractValuesForSimMCS,
  mapFormDataToDefaultData6DOF,
  parseStringToNumberArray,
  prepareData6DOF,
  transformDataVersionsToOptions,
} from "src/utils/common";
import Loading from "src/components/Common/Loading";
import { isEmpty, merge } from "lodash";
import { sim6DOFDefaultData } from "src/components/GNCSimulator6DOF/form/defaultData";
import LoadingWrapper from "src/components/Common/Table/Loading";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";

const GNCSimulatorDetail6DOF = () => {
  const { t } = useTranslation("gnc-simulator-6dof");
  const navigate = usePatchedNavigate();
  const showAlert = useSetRecoilState(alertState);
  const [jsonValue, setJsonValue] = useRecoilState(dataJson);
  const isUploadingJsonFile = useRecoilValue(uploadingJsonFile);
  const [defaultData, setDefaultData] = useState(sim6DOFDefaultData);
  const { id } = useParams();
  let [searchParams] = useSearchParams();
  const formAction = searchParams.get("action");
  const page = searchParams.get("page");
  const condition = searchParams.get("condition");
  const projectId = searchParams.get("projectId");
  const tag_ids = searchParams.get("tag_ids");

  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  const [isLoadingVersion, setIsLoadingVersion] = useState(true);

  const getRedirectURL = useCallback(
    (pageIndex) => {
      if (condition) {
        return `${APP_ROUTES.GNC_SIMULATOR_6DOF}?id=${id}${page ? "&page=" + page : ""}${condition ? "&condition=" + condition : ""}${
          projectId ? "&projectId=" + projectId : ""
        }${tag_ids ? "&tag_ids=" + tag_ids : ""}`;
      }
      return `${APP_ROUTES.GNC_SIMULATOR_6DOF}?id=${id}${pageIndex ? "&page=" + pageIndex : ""}${
        projectId ? "&projectId=" + projectId : ""
      }${tag_ids ? "&tag_ids=" + tag_ids : ""}`;
    },
    [id, page, condition]
  );

  const { data, mutate: getSimulatorDetailMutate } = useMutation({
    mutationFn: () => getSimulatorsDetail6DOF(id),
    onSettled: () => setIsLoadingDetail(false),
    onSuccess: (data) => {
      if (data?.status === PROCESS_STATUS.SUCCEEDED && formAction !== FORM_TYPE.COPY) {
        return navigate(APP_ROUTES.NOT_FOUND);
      }
      if (data?.status === PROCESS_STATUS.FAILED && formAction !== FORM_TYPE.EDIT) {
        return navigate(APP_ROUTES.NOT_FOUND);
      }
    },
    onError: (error) => {
      if (error?.response?.status === 404) {
        return navigate(APP_ROUTES.NOT_FOUND);
      }
    },
  });

  const { data: versions } = useQuery(["getGNCVersions"], () => getSimulatorOptions(SIMULATION_TYPE.GNC_6DOF), {
    refetchOnWindowFocus: false,
    onSettled: () => setIsLoadingVersion(false),
  });

  const createSimulatorMutate = useMutation({
    mutationFn: ({ data }) => createSimulator6DOF({ data }),
  });

  const editSimulatorMutate = useMutation({
    mutationFn: ({ data }) => editSimulator6DOF({ data }),
  });

  const getPageIndexSimulator6DOFMutate = useMutation({
    mutationFn: (id) => getPageIndexSimulator6DOF(id),
  });

  const processRootData = (data, id) => {
    const isSimCopy = formAction === FORM_TYPE.COPY;
    const processAsRoot = data?.processAsRoot;
    delete data?.processAsRoot;
    delete data?.parent;

    if (processAsRoot) {
      Object.assign(data, { parent: null, parent_id: null });
      return;
    }

    if (isSimCopy) {
      data.parent_id = Number(id);
    }
  };

  const handleRedirectURLSimParent = (id) => {
    const isSimCopy = formAction === FORM_TYPE.COPY;
    if (isSimCopy) {
      window.open(`${APP_ROUTES.GNC_SIMULATOR_6DOF}?id=${id}&page=${data?.page}&projectId=${projectId}`, "_blank");
      return;
    }

    getPageIndexSimulator6DOFMutate.mutate(id, {
      onSuccess: (pageIndexData) => {
        window.open(`${APP_ROUTES.GNC_SIMULATOR_6DOF}?id=${id}&page=${pageIndexData?.page}&projectId=${projectId}`, "_blank");
      },
    });
  };

  const submitData = ({ data }, callback) => {
    if (data?.type === 1) {
      data.MCS.sigma_I_e = parseStringToNumberArray(data.MCS.sigma_I_e_x, data.MCS.sigma_I_e_y, data.MCS.sigma_I_e_z);
      data.MCS.sigma_I_s = parseStringToNumberArray(data.MCS.sigma_I_s_x, data.MCS.sigma_I_s_y, data.MCS.sigma_I_s_z);
    }

    const dataConvertStringToNumber = mapFormDataToDefaultData6DOF(data, defaultData);
    const page = data?.page;
    delete dataConvertStringToNumber?.page;
    processRootData(dataConvertStringToNumber, id);

    const keysToDelete = ["sigma_I_e_x", "sigma_I_e_y", "sigma_I_e_z", "sigma_I_s_x", "sigma_I_s_y", "sigma_I_s_z"];

    keysToDelete.forEach((key) => {
      delete dataConvertStringToNumber?.input_data?.MCS?.[key];
    });

    if (formAction === FORM_TYPE.EDIT) {
      dataConvertStringToNumber.id = id;
      return editSimulatorMutate.mutate(
        { data: dataConvertStringToNumber },
        {
          onSuccess: () => {
            showAlert({
              status: true,
              message: t("new.retry_success"),
              type: "success",
            });
            navigate(getRedirectURL(page));
          },
          onError: () => {
            const isSubmitFailed = true;
            callback(isSubmitFailed);
          },
          onSettled: () => {
            const isSubmitFailed = false;
            callback(isSubmitFailed);
          },
        }
      );
    }

    createSimulatorMutate.mutate(
      { data: dataConvertStringToNumber },
      {
        onSuccess: () => {
          showAlert({
            status: true,
            message: t("new.create_success"),
            type: "success",
          });

          navigate(APP_ROUTES.GNC_SIMULATOR_6DOF);
        },
        onError: () => {
          const isSubmitFailed = true;
          callback(isSubmitFailed);
        },
        onSettled: () => {
          const isSubmitFailed = false;
          callback(isSubmitFailed);
        },
      }
    );
  };

  useEffect(() => {
    getSimulatorDetailMutate();
  }, []);

  useEffect(() => {
    if (!isEmpty(jsonValue)) {
      setDefaultData((oldVal) => ({
        ...oldVal,
        input_data: merge({}, sim6DOFDefaultData.input_data, jsonValue),
      }));
    }
  }, [jsonValue]);

  useEffect(() => {
    if (!isEmpty(data)) {
      if (data?.type === SIMULATION_TYPE.GNC_6DoF_MCS) {
        extractValuesForSimMCS("sigma_I_s", data?.input_data?.MCS, ["x", "y", "z"], data?.input_data);
        extractValuesForSimMCS("sigma_I_e", data?.input_data?.MCS, ["x", "y", "z"], data?.input_data);
      }

      setDefaultData({
        name: data?.name,
        type: data?.type === SIMULATION_TYPE.GNC_6DoF_MCS ? 1 : 0,
        version_name: data?.version_name,
        note: data?.note,
        series: data?.series,
        input_data: merge({}, defaultData?.input_data, data?.input_data),
        parent: data?.parent || null,
        page: data?.page,
      });
    }
  }, [data]);

  useEffect(() => {
    return () => {
      setJsonValue(null);
    };
  }, []);

  const transformDetailData = (data) => {
    data.series = Array.isArray(data.series) ? data.series : [data.series];
    data.series.forEach((series) => {
      const paths = ["parameter_path", "aerodynamic_coefficients_path"];
      paths.forEach((path) => {
        series[path] = isEmpty(series[path]) ? [] : Array.isArray(series[path]) ? series[path] : [series[path]];
      });
    });
    return {
      ...data,
      currentName: data?.name,
      name: formAction === FORM_TYPE.EDIT ? data?.name : `Copy of ${data?.name}`,
    };
  };

  if (isLoadingVersion || isLoadingDetail || !defaultData?.name) return <Loading />;

  return (
    <>
      <LoadingWrapper loading={getPageIndexSimulator6DOFMutate.isLoading} customClass="loading-detail-page">
        <FormGNCSimulator6DOF
          submitData={submitData}
          data={transformDetailData(prepareData6DOF(defaultData))}
          title={t("new.title_detail")}
          isUploadingJsonFile={isUploadingJsonFile}
          getRedirectURL={getRedirectURL}
          versions={transformDataVersionsToOptions(versions || [])}
          handleRedirectURLSimParent={handleRedirectURLSimParent}
        />
      </LoadingWrapper>
    </>
  );
};

export default routeGuard(GNCSimulatorDetail6DOF, RESOURCE_TYPE.GNC_SIMULATION, ROLES.EDITOR);
